/**
 * My Assignments Query Hook
 *
 * Single query over the crew member's assignments with polling. Shares its
 * query key with useCrewDashboard's assignments query, so responding to an
 * assignment from either the dashboard or the My Assignments page updates
 * both views without a manual reload.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMyAssignments, type Assignment } from '../../api/assignments';
import { queryKeys, POLL_INTERVALS } from '../../lib/queryClient';
import { getErrorMessage } from '../../api/client';
import { useOrganization } from '../../context/OrganizationContext';

interface UseMyAssignmentsOptions {
  /** Enable/disable automatic polling (default: true) */
  polling?: boolean;
  /** Custom polling interval in ms (default: 30000) */
  pollingInterval?: number;
}

export function useMyAssignments(options: UseMyAssignmentsOptions = {}) {
  const { polling = true, pollingInterval = POLL_INTERVALS.DASHBOARD } = options;
  const { currentOrg, isLoading: orgLoading } = useOrganization();

  const query = useQuery({
    queryKey: [...queryKeys.assignments.my(), currentOrg?.orgSlug],
    queryFn: () => getMyAssignments(),
    enabled: !orgLoading && !!currentOrg,
    refetchInterval: polling ? pollingInterval : false,
    refetchIntervalInBackground: false,
    staleTime: pollingInterval / 2,
  });

  return {
    assignments: (query.data?.assignments ?? []) as Assignment[],
    loading: orgLoading || query.isLoading,
    error: query.error ? getErrorMessage(query.error) : null,
    isRefetching: query.isRefetching,
    dataUpdatedAt: query.dataUpdatedAt,
    refetch: query.refetch,
  };
}

/**
 * Invalidate the my-assignments query (all orgs) after a mutation. Prefix
 * match also hits useCrewDashboard's copy of the query.
 */
export function useInvalidateMyAssignments() {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.assignments.my() });
}
